/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Networking", description = "Allow Hightouch to connect to your warehouse, destinations, and other internal services.", lastUpdatedDate = new Date(1792540800000), keywords = "tls, tls 1.2, transport layer security, ssl";
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Networking",
        description: "Allow Hightouch to connect to your warehouse, destinations, and other internal services.",
        lastUpdatedDate: "Wed Oct 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Choose a connection method",
          "depth": 0,
          "id": "choose-a-connection-method"
        }, {
          "value": "IP addresses",
          "depth": 0,
          "id": "ip-addresses"
        }, {
          "value": "Amazon Web Services",
          "depth": 1,
          "id": "amazon-web-services"
        }, {
          "value": "Google Cloud Platform",
          "depth": 1,
          "id": "google-cloud-platform"
        }, {
          "value": "Microsoft Azure",
          "depth": 1,
          "id": "microsoft-azure"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      img: "img",
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      em: "em",
      h3: "h3",
      code: "code"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.p, {
        children: ["Hightouch can connect directly to services running within your private network, including your data warehouse, self-hosted destinations, and even tools like a ", _jsx(_components.a, {
          href: "/extensions/git-sync",
          children: "private Git server"
        }), " or your own ", _jsx(_components.a, {
          href: "/sources/looker",
          children: "hosted Looker deployment"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "get-started/connection-types-comparison.png",
          alt: "Direct connection vs. SSH tunnel"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For added security, consider using an ", _jsx(_components.a, {
          href: "/security/ssh-tunneling",
          children: "SSH tunnel"
        }), " to layer on public key-based authentication and encryption."]
      }), "\n", _jsx(_components.h2, {
        id: "choose-a-connection-method",
        children: "Choose a connection method"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports three ways to reach services inside your network. You can start with one method and move to another later."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Method"
            }), _jsx(_components.th, {
              children: "When to use it"
            }), _jsx(_components.th, {
              children: "What you need"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#ip-addresses",
                children: "IP allowlisting"
              })
            }), _jsx(_components.td, {
              children: "Your service is reachable from the public internet."
            }), _jsx(_components.td, {
              children: "Add Hightouch's IP addresses for your region to your firewall allowlist."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/security/ssh-tunneling",
                children: "SSH tunneling"
              })
            }), _jsx(_components.td, {
              children: "Your service is on a private network."
            }), _jsx(_components.td, {
              children: "A bastion host reachable from the internet (standard tunnel), or an SSH connection you keep open to Hightouch (reverse tunnel)."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: ["Private connectivity: ", _jsx(_components.a, {
                href: "/security/aws/privatelink",
                children: "AWS PrivateLink"
              }), ", ", _jsx(_components.a, {
                href: "/security/gcp/private-service-connect",
                children: "GCP Private Service Connect"
              }), ", or ", _jsx(_components.a, {
                href: "/security/azure/private-link",
                children: "Azure Private Link"
              })]
            }), _jsx(_components.td, {
              children: "You want traffic between Hightouch and your service to stay off the public internet."
            }), _jsx(_components.td, {
              children: "A Business tier workspace and a supported service. Each page lists the services it supports."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "ip-addresses",
        children: "IP addresses"
      }), "\n", _jsx(_components.p, {
        children: "Many private networks need to be configured to allow external connections. You can provide Hightouch access by adding Hightouch's IP addresses to your firewall allowlist."
      }), "\n", _jsxs(_components.p, {
        children: ["Ensure you configure your network firewall rules to allow access from ", _jsx(_components.em, {
          children: "only"
        }), " the IP addresses corresponding to the ", _jsx(_components.a, {
          href: "/security/regions",
          children: "region"
        }), " you selected when you created your Hightouch workspace."]
      }), "\n", _jsx(_components.h3, {
        id: "amazon-web-services",
        children: "Amazon Web Services"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Region"
            }), _jsx(_components.th, {
              children: "IP Addresses"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ap-south-1 (Mumbai, India)"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "13.126.190.7"
              }), _jsx("br", {}), _jsx(_components.code, {
                children: "13.201.148.238"
              }), _jsx("br", {}), _jsx(_components.code, {
                children: "65.1.90.146"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ap-southeast-2 (Sydney, Australia)"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "3.104.61.99"
              }), _jsx("br", {}), _jsx(_components.code, {
                children: "13.54.27.62"
              }), _jsx("br", {}), _jsx(_components.code, {
                children: "13.238.99.151"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "eu-west-1 (Ireland)"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "34.255.226.212"
              }), _jsx("br", {}), _jsx(_components.code, {
                children: "52.16.84.199"
              }), _jsx("br", {}), _jsx(_components.code, {
                children: "52.211.114.138"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "us-east-1 (N. Virginia, USA)"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "54.196.30.169"
              }), _jsx("br", {}), _jsx(_components.code, {
                children: "52.72.201.213"
              }), _jsx("br", {}), _jsx(_components.code, {
                children: "18.213.226.96"
              }), _jsx("br", {}), _jsx(_components.code, {
                children: "3.224.126.197"
              }), _jsx("br", {}), _jsx(_components.code, {
                children: "3.217.26.199"
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "google-cloud-platform",
        children: "Google Cloud Platform"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Region"
            }), _jsx(_components.th, {
              children: "IP Addresses"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "europe-west1 (Belgium)"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "35.241.218.183"
              }), _jsx("br", {}), _jsx(_components.code, {
                children: "35.195.167.179"
              }), _jsx("br", {}), _jsx(_components.code, {
                children: "104.199.61.185"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "me-central2 (Dammam, Saudi Arabia)"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "34.166.244.44"
              }), _jsx("br", {}), _jsx(_components.code, {
                children: "34.166.247.32"
              }), _jsx("br", {}), _jsx(_components.code, {
                children: "34.166.115.20"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "us-east4 (N. Virginia, USA)"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "34.145.155.148"
              }), _jsx("br", {}), _jsx(_components.code, {
                children: "35.245.150.71"
              }), _jsx("br", {}), _jsx(_components.code, {
                children: "35.245.58.91"
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "microsoft-azure",
        children: "Microsoft Azure"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Region"
            }), _jsx(_components.th, {
              children: "IP Addresses Ranges"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "East US (N. Virginia)"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "23.100.16.164/30"
              })
            })]
          })
        })]
      })]
    });
  }
}
export default MDXContent;
